import { UserPhoneType, SubscribeToAlertsTaskData, CommunicationSubscriptionChannel } from '@centecomm/portal-service';
import { useUsers, useUserTasks, useCurrentUser, useCommunicationPlans } from '@centecomm/portal-service-react';
import { Form, Formik } from 'formik';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { IconColors, Remove } from '../../../Icons';
import { Button } from '../../../Shared/Button';
import { FlexWrapper } from '../../../Shared/FlexWrapper';
import { FormButtons, FormInputs } from '../../../Shared/Forms';
import { globalMessageActionCreators } from '../../../State';
import { TELEPHONE_REGEXP } from '../../../utils/formatPhone';

const SubscribeToAlert: React.FC<{ task: SubscribeToAlertsTaskData }> = ({ task }): React.ReactElement => {
    const { t } = useTranslation(['tasks']);
    const dispatch = useDispatch();
    const { updateUserPhone } = useUsers();
    const { dismissTask } = useUserTasks();
    const { currentUserId: userId } = useCurrentUser();
    const { createCommunicationSubscription } = useCommunicationPlans();

    const [showAction, setShowAction] = React.useState<boolean>(false);

    const EMERGENCY_ALERTS_COMMUNICATION_PLAN_ID = 1;

    const handleSubmit = async (mobilePhone: string) => {
        if (!userId || userId < 0) return;

        await updateUserPhone(userId as number, UserPhoneType.Mobile, mobilePhone).catch((err) => {
            dispatch(
                globalMessageActionCreators.addErrorMessage(t('tasks:subscribeToAlertSystem.updateUserPhoneErrorMessage')),
            );

            throw new Error(err);
        });

        await createCommunicationSubscription(
            EMERGENCY_ALERTS_COMMUNICATION_PLAN_ID,
            userId,
            CommunicationSubscriptionChannel.SMS,
        ).catch((err) => {
            dispatch(
                globalMessageActionCreators.addErrorMessage(
                    t('tasks:subscribeToAlertSystem.createCommunicationErrorMessage'),
                ),
            );

            throw new Error(err);
        });

        dispatch(
            globalMessageActionCreators.addSuccessMessage(
                t('tasks:subscribeToAlertSystem.createCommunicationSuccessMessage'),
            ),
        );

        dismissTask(task.id, task.type);
    };

    return (
        <>
            <p style={{ fontSize: '1rem', margin: '-0.5rem 0 1.25rem', maxWidth: '45rem' }}>
                {t('tasks:subscribeToAlertSystem.tenantSubscribeMessage')}
            </p>
            <Button
                callback={() => setShowAction(!showAction)}
                text={t('tasks:subscribeToAlertSystem.updateSubscription')}
            />
            {showAction && (
                <div
                    style={{
                        margin: '1rem 0',
                        padding: '1rem',
                        border: `1px solid ${IconColors.OffWhite}`,
                        borderRadius: '0.25rem',
                    }}
                >
                    <h3
                        style={{
                            fontSize: '1.15rem',
                            lineHeight: '1.15rem',
                            margin: '0 0 0.75rem',
                        }}
                    >
                        {t('tasks:subscribeToAlertSystem.optIn')}
                    </h3>
                    <p
                        style={{
                            fontSize: '0.875rem',
                            color: IconColors.DarkGrey,
                            margin: '0 0 0.5rem',
                            lineHeight: '1.5rem',
                        }}
                    >
                        {t('tasks:subscribeToAlertSystem.verifyPhoneMessage')}
                    </p>
                    <p
                        style={{
                            fontSize: '0.875rem',
                            color: IconColors.DarkGrey,
                            margin: '0 0 1.5rem',
                            lineHeight: '1.5rem',
                        }}
                    >
                        {t('tasks:subscribeToAlertSystem.forMoreInformation')}{' '}
                        <Link to="/account" style={{ color: IconColors.Action }}>
                            {t('tasks:subscribeToAlertSystem.account')}
                        </Link>{' '}
                        {t('tasks:subscribeToAlertSystem.page')}.
                    </p>
                    <Formik
                        validateOnMount
                        initialValues={{
                            mobilePhone: task.mobilePhone || '',
                            subscribeToAlerts: false,
                        }}
                        onSubmit={async (values) => handleSubmit(values.mobilePhone)}
                        validationSchema={Yup.object({
                            mobilePhone: Yup.string()
                                .required(t('tasks:subscribeToAlertSystem.validation.phoneNumberRequired'))
                                .matches(
                                    new RegExp(TELEPHONE_REGEXP),
                                    t('tasks:subscribeToAlertSystem.validation.enterAValidPhoneNumber'),
                                ),
                            subscribeToAlerts: Yup.boolean()
                                .required(t('tasks:subscribeToAlertSystem.validation.youMustCheckTheBoxToSubscribe'))
                                .isTrue(t('tasks:subscribeToAlertSystem.validation.youMustCheckTheBoxToSubscribe')),
                        })}
                    >
                        {({ isSubmitting, errors, isValid, isValidating }) => (
                            <Form style={{ maxWidth: '40rem' }}>
                                <FlexWrapper align="center" justify="between">
                                    <FormInputs.Telephone
                                        id="mobilePhone"
                                        name="mobilePhone"
                                        label={t('tasks:subscribeToAlertSystem.mobilePhone')}
                                        placeholder={t('tasks:subscribeToAlertSystem.enterMobilePhone')}
                                        hideLabel
                                        fullWidth
                                    />
                                    <div style={{ marginLeft: '0.5rem' }}>
                                        <FormButtons.Submit
                                            text={t('tasks:subscribeToAlertSystem.subscribe')}
                                            disable={isSubmitting || !isValid || isValidating}
                                            inverse
                                        />
                                    </div>
                                </FlexWrapper>
                                <FlexWrapper
                                    align="center"
                                    justify="center"
                                    style={{
                                        margin: '0.5rem 0 0',
                                        padding: '1rem',
                                        width: '100%',
                                        backgroundColor: IconColors.OffWhite,
                                    }}
                                >
                                    {!errors.mobilePhone ? (
                                        <FormInputs.CheckBox
                                            id="subscribeToAlerts"
                                            name="subscribeToAlerts"
                                            display={t('tasks:subscribeToAlertSystem.subscribeToEmergencyAlerts')}
                                            required
                                        />
                                    ) : (
                                        <p>{t('tasks:subscribeToAlertSystem.enterMobileNumberToReceiveAlerts')}</p>
                                    )}
                                </FlexWrapper>
                                <button
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        marginTop: '0.5rem',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                    }}
                                    onClick={() => dismissTask(task.id, task.type)}
                                    type="button"
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Remove aspect="1rem" color={IconColors.DarkGrey} />
                                        <p style={{ color: IconColors.DarkGrey, marginLeft: '0.5rem' }}>
                                            {t('tasks:subscribeToAlertSystem.noThanksMobilePhone')}
                                        </p>
                                    </div>
                                </button>
                            </Form>
                        )}
                    </Formik>
                </div>
            )}
        </>
    );
};

export default SubscribeToAlert;
